import { useCallback } from 'react';
import { HomeChatHttpError } from './api';
import type { ChatMessage, ChatSession } from './types';
import type { FarRegime } from '../FarChat/types';
import { FastAnswerRecoveryError } from '../FarChat/types';
import { resetInFlightTurnState } from '../chatTurnState';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { REFUSAL_COPY, friendlyTurnErrorMessage, isTurnInProgressError, isExpectedCancellationError, isUnavailableWorkflowError } from './HomeChatTurnErrors';
import { INTENT_CHOICE_UNAVAILABLE_COPY } from './homeChatChoiceUtils';
import { PENDING_ID, makeRequestId } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatSessions } from './useHomeChatSessions';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import type { HomeChatTurnSubmission } from './useHomeChatTurnSubmission';
import type { HomeChatHydration } from './useHomeChatHydration';


type Inputs = Pick<HomeChatPanelState,
  | 'inFlight'
  | 'pendingClarification'
  | 'interactionRevisionRef'
  | 'setInFlightUserMsg'
  | 'setInFlightClarification'
  | 'setInFlightTopicFocus'
  | 'setPendingRequestIdSynced'
  | 'setTurns'
  | 'setPendingClarification'
  | 'setInFlight'
  | 'scrollThreadToLatest'
  | 'activeSessionId'
  | 'messages'
  | 'turns'
  | 'activeSessionIdRef'
  | 'setActiveSessionId'
  | 'setSessions'
  | 'setMessages'
  | 'pendingTopicClarification'
  | 'setPendingTopicClarification'
> &
Pick<Props, 'onTurnStart'> &
Pick<HomeChatSessions,
  | 'insertPendingPlaceholder'
  | 'ensureSession'
> &
Pick<HomeChatSessionReconciliation,
  | 'scheduleInFlightTitleRefresh'
  | 'refreshAfterTurnDispatch'
  | 'refreshSessions'
  | 'reconcileExpectedCancellation'
> &
Pick<HomeChatTurnSubmission,
  | 'sendLazyCreateV2Turn'
  | 'sendV2Turn'
  | 'runPrimingTurn'
> &
Pick<HomeChatHydration,
  | 'isStaleResolve'
  | 'notify'
  | 'clearPendingIfOwned'
>;

export function useHomeChatComposer({
  inFlight,
  pendingClarification,
  onTurnStart,
  interactionRevisionRef,
  setInFlightUserMsg,
  setInFlightClarification,
  setInFlightTopicFocus,
  setPendingRequestIdSynced,
  setTurns,
  setPendingClarification,
  setInFlight,
  scrollThreadToLatest,
  activeSessionId,
  insertPendingPlaceholder,
  scheduleInFlightTitleRefresh,
  messages,
  turns,
  ensureSession,
  sendLazyCreateV2Turn,
  refreshAfterTurnDispatch,
  isStaleResolve,
  activeSessionIdRef,
  setActiveSessionId,
  setSessions,
  sendV2Turn,
  refreshSessions,
  reconcileExpectedCancellation,
  setMessages,
  notify,
  clearPendingIfOwned,
  runPrimingTurn,
  pendingTopicClarification,
  setPendingTopicClarification,
}: Inputs) {

  const handleSend = useCallback(
    async (text: string, finalQuestion?: string, regime?: FarRegime) => {
      const trimmed = text.trim();
      if (!trimmed || inFlight) return;

      // A new free-form send (as opposed to the card's explicit
      // final-question resolve) supersedes this card. Keep a snapshot until
      // the server accepts the replacement; `sendV2Turn` then inserts the
      // same fixed status turn that the backend persisted.
      const supersededClarification = finalQuestion === undefined
        ? pendingClarification
        : null;
      onTurnStart?.();
      interactionRevisionRef.current += 1;

      const userMsg: ChatMessage = { role: 'user', content: trimmed };
      // Hold the question in `inFlightUserMsg` so it renders as an
      // optimistic user bubble BELOW any prior turns. This avoids a
      // follow-up question jumping above prior turns while a v2 turn is
      // in flight (since `messages` renders before `turns` in JSX).
      setInFlightUserMsg(trimmed);
      // Hold the clarified text (final ≠ original) so the "Clarified: …"
      // callout renders in the in-flight area. Fresh sends pass no
      // `finalQuestion` → null. Clarification resolution normally routes
      // through `runPrimingTurn`, but mirror the behaviour here for safety.
      setInFlightClarification(
        finalQuestion && finalQuestion !== trimmed ? finalQuestion : null
      );
      // Fresh sends carry no topic focus; clear any stale in-flight focus.
      setInFlightTopicFocus(null);
      // Generate a fresh client_request_id per send. The backend echoes
      // it back; the StepProgressChecklist subscribes via SSE.
      const crid = makeRequestId();
      setPendingRequestIdSynced(crid);
      // The server atomically persists a fixed status turn before running a
      // free-form replacement. Project that transition at dispatch time:
      // waiting for the replacement response created a visible gap where the
      // abandoned question disappeared from this live thread. A later thread
      // hydration replaces this optimistic record with the server-owned turn.
      if (supersededClarification) {
        setTurns((previous) => [...previous, {
          turn_id: `clarification-abandoned-pending-${crid}`,
          user_msg: supersededClarification.original_query,
          summary: 'This question was abandoned',
          detail: '',
          citations: [],
          // This deterministic transcript status deliberately reuses the
          // established refusal/warning bubble. It is not an answer without
          // FAR/RFO citations, so it must not render the ungrounded notice.
          is_refusal: true,
          refusal_text: 'This question was abandoned',
        }]);
        // The transcript status now owns the old question; avoid retaining
        // its obsolete interactive card while the replacement is pending.
        setPendingClarification(null);
      }
      setInFlight(true);

      // The optimistic user bubble and loading checklist have just been
      // scheduled; request the scroll after that DOM commit.
      scrollThreadToLatest();

      // Optimistic sidebar entry for the DIRECT query-bar submit. When the
      // user types a question straight into the bar on the homepage / no-
      // session state (WITHOUT first clicking "+ New chat"), this is a lazy-
      // create. Insert the SAME `PENDING_ID` "New Chat" rail placeholder the
      // "+ New chat" button uses so the entry shows up INSTANTLY on submit —
      // not only after the lazy-create response + merge/refresh lands. The
      // existing flow then takes over: `ensureSession` (PENDING path) or the
      // lazy-create response swaps `PENDING_ID` → the real session id in
      // place, the title agent fills the title, and the merge keeps the row
      // continuously present. Guarded to `activeSessionId === null` so we
      // never insert for a real active session; `insertPendingPlaceholder`'s
      // own de-dup makes the PENDING-already-present case (the user clicked
      // "+ New chat" first, then typed) a no-op — no double entry.
      if (activeSessionId === null) {
        insertPendingPlaceholder();
      }

      // In-flight rail refresh: the backend runs the title agent in PARALLEL
      // with the relevance+clarification gate and persists the generated
      // title the moment the gate resolves — BEFORE the slower reader/synth
      // round-trips finish. One bounded refresh ~1.5s after submit picks the
      // title up so the active "New Chat" rail entry fills in BEFORE the
      // answer lands (the merge keeps the same continuously-present entry —
      // no flash, no reload). One-shot, not a loop. handleProgressComplete
      // still refreshes after the answer as a backstop for slow summarizers.
      scheduleInFlightTitleRefresh(crid);

      // Early-warning: a follow-up send (messages or turns already exist)
      // should never resolve to 'new' — that means we lost the session id
      // somewhere between priming and this send. Production bug fingerprint
      // for the cookie-chatbot regression. Logged so we can correlate with
      // any /sessions/new/turn requests in network logs.
      if (
        !activeSessionId &&
        (messages.length > 0 || turns.length > 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          '[homechat] follow-up sending to "new" but messages exist; activeSessionId was lost',
          {
            messagesCount: messages.length,
            turnsCount: turns.length,
            firstUserMsg:
              messages.find((m) => m.role === 'user')?.content?.slice(0, 50) ??
              turns.find((t) => t.user_msg)?.user_msg?.slice(0, 50),
          }
        );
      }

      try {
        // Resolve to a target id for the turn:
        //  - real active session → use it
        //  - PENDING_ID placeholder → ensureSession() POSTs /sessions
        //    once and adopts the new id before this turn fires
        //  - no selection at all → use v2's lazy-create route, which
        //    creates the session and runs the turn atomically
        let target: string;
        if (activeSessionId === PENDING_ID) {
          try {
            target = await ensureSession();
          } catch {
            // Network-failed lazy-create: still let the turn go through
            // by handing 'new' to the server.
            target = 'new';
          }
        } else {
          target = activeSessionId ?? 'new';
        }

        // For 'new' targets, v2 lazy-create mints a session and runs the
        // multi-part pipeline in one call. Real session ids use v2 directly.
        if (target === 'new') {
          const lazyTurn = sendLazyCreateV2Turn(
            trimmed,
            crid,
            finalQuestion,
            regime
          );
          refreshAfterTurnDispatch();
          const newSid = await lazyTurn;
          // Stale-resolve guard: skip adopting the new id if the user
          // navigated away while the lazy-create was in flight.
          if (
            !isStaleResolve(crid) &&
            newSid &&
            newSid !== activeSessionId
          ) {
            // Sync the merge ref synchronously so the `refreshSessions`
            // below keeps the just-minted (still-turn-less) session in the
            // rail rather than letting the server zero-turn filter drop it.
            activeSessionIdRef.current = newSid;
            setActiveSessionId(newSid);
            // Swap the optimistic `PENDING_ID` placeholder for the real
            // (still-turn-less) session id IN PLACE — keep its position and
            // any timestamps — so the rail row never flashes and we don't
            // leave a duplicate `PENDING_ID` row alongside the new id. This
            // mirrors `ensureSession`'s in-place swap for the PENDING path;
            // on the direct-submit lazy-create path `ensureSession` isn't
            // called (target was 'new'), so the swap happens here instead.
            setSessions((prev) => {
              const placeholder = prev.find(
                (s) => s.session_id === PENDING_ID
              );
              if (!placeholder) return prev;
              const realEntry: ChatSession = {
                session_id: newSid,
                title: placeholder.title ?? null,
                created_at: placeholder.created_at,
                last_activity_at: placeholder.last_activity_at,
                message_count: placeholder.message_count,
              };
              return prev.map((s) =>
                s.session_id === PENDING_ID
                  ? realEntry
                  : s
              )
                // Guard: if `newSid` already exists (e.g. a racing
                // refreshSessions already merged it in), drop the swapped
                // duplicate so only one row for `newSid` remains.
                .filter(
                  (s, idx, arr) =>
                    arr.findIndex(
                      (o) => o.session_id === s.session_id
                    ) === idx
                );
            });
          }
        } else {
          const turn = sendV2Turn(
            target,
            trimmed,
            crid,
            finalQuestion,
            regime,
            undefined,
          );
          refreshAfterTurnDispatch();
          await turn;
        }
        // Refresh sessions list to surface auto-title + new ordering.
        void refreshSessions();
      } catch (e: any) {
        // A newer request (including an edit/replacement) owns the active
        // branch now. A late cancellation from this request is not allowed
        // to reconcile its obsolete snapshot over that newer branch.
        if (isStaleResolve(crid)) return;
        if (isExpectedCancellationError(e)) {
          await reconcileExpectedCancellation(crid);
          return;
        }
        const msg: string = e?.message || '';
        // The optimistic bubble is intentionally transient; once a request
        // fails there is no Turn to replace it. Retain a normal user message
        // so the submission is never erased when in-flight state is cleared.
        setMessages((prev) => [...prev, userMsg]);
        // Only the server's actual turn-admission conflict is a busy state.
        // Other 409 responses, such as an unavailable workflow recommendation,
        // require their own user-facing explanation.
        if (isTurnInProgressError(e)) {
          notify(
            'warning',
            'Another response is in progress. Try again in a few seconds.'
          );
        } else if (e instanceof FastAnswerRecoveryError) {
          notify('error', e.message);
        } else if (isUnavailableWorkflowError(e)) {
          notify('warning', INTENT_CHOICE_UNAVAILABLE_COPY);
        } else if (/503|llm_unavailable|reference_data_unavailable/i.test(msg)) {
          // Refusal-shaped assistant message in-thread (mirrors workflow chat).
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: REFUSAL_COPY,
              refusal: true,
              refusal_reason: /llm_unavailable/i.test(msg)
                ? 'llm_unavailable'
                : 'reference_data_unavailable',
            } as ChatMessage,
          ]);
        } else if (e instanceof HomeChatHttpError && e.status === 500) {
          // Masked reader crash: backend now returns 500 with a friendly
          // `detail.error`. Render it inline as a refusal-styled bubble
          // (never the error_code / technical detail).
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: friendlyTurnErrorMessage(e),
              refusal: true,
              refusal_reason: 'turn_failed',
            } as ChatMessage,
          ]);
        } else {
          notify('error', 'Failed to send message.');
        }
        scrollThreadToLatest();
      } finally {
        // §4.1 state hygiene — IN-FLIGHT teardown via the authoritative
        // shared reset. Clears the four in-flight fields but DELIBERATELY
        // leaves `pendingClarification` untouched (on the vague path it was
        // just set to render the ClarificationCard and must survive this
        // finally). `pendingRequestId` is cleared via `clearPendingIfOwned`.
        // `finalQuestion` was a per-call arg and is dropped here, so the NEXT
        // send (a fresh user message) omits `final_question` and the
        // clarification gate runs again. If it leaked, the backend would skip
        // the gate and a fresh vague question would never get clarified.
        // `pendingClarification` is reset on the answer/resolve paths and in
        // `handleProgressComplete`.
        if (!isStaleResolve(crid)) {
          resetInFlightTurnState({
            setInFlight,
            clearPendingRequestId: () => clearPendingIfOwned(crid),
            setInFlightUserMsg,
            setInFlightClarification,
            setInFlightTopicFocus,
          });
        }
      }
    },
    [
      activeSessionId,
      inFlight,
      pendingClarification,
      onTurnStart,
      messages,
      turns,
      notify,
      refreshSessions,
      reconcileExpectedCancellation,
      ensureSession,
      sendLazyCreateV2Turn,
      sendV2Turn,
      isStaleResolve,
      clearPendingIfOwned,
      setPendingRequestIdSynced,
      scheduleInFlightTitleRefresh,
      refreshAfterTurnDispatch,
      insertPendingPlaceholder,
      scrollThreadToLatest,
    ]
  );

  // Resolve a live clarification: resubmit the ORIGINAL query as the
  // message and the chosen text as `final_question`. We route through
  // `runPrimingTurn` with a CONCRETE target (the session id stored on the
  // envelope) rather than `handleSend`, because `handleSend` reads
  // `activeSessionId` from a possibly-stale closure — on the lazy-create
  // path the backend already minted the real session and returned its id
  // on the `clarification_needed` envelope, so we target it directly
  // (NOT another lazy-create). `runPrimingTurn` ignores `activeSessionId`
  // and dispatches against the explicit target. The card is dismissed
  // immediately so it doesn't linger beneath the loading checklist.
  const handleResolveClarification = useCallback(
    (finalQuestion: string, regime: FarRegime) => {
      const pc = pendingClarification;
      const original = pc?.original_query ?? finalQuestion;
      const target = pc?.session_id || activeSessionId || 'new';
      if (pc?.session_id && pc.session_id !== activeSessionId) {
        setActiveSessionId(pc.session_id);
      }
      setPendingClarification(null);
      void runPrimingTurn(
        target,
        original,
        finalQuestion,
        regime
      );
    },
    [pendingClarification, activeSessionId, runPrimingTurn]
  );

  // Resolve a live topic-focus card: resubmit against the SAME session
  // (carried on the envelope) with `focus_token` + chosen `topicFocus`. We
  // route through `runPrimingTurn` with the concrete target for the same
  // closure-staleness reasons as `handleResolveClarification`. We carry the
  // envelope's `original_query` as the `final_question` expiry fallback per
  // spec §6.5. The card is dismissed immediately.
  const handleResolveTopicFocus = useCallback(
    (topicFocus: string) => {
      const env = pendingTopicClarification;
      const original = env?.original_query ?? '';
      const focusToken = env?.focus_token;
      const regime = env?.regime;
      const target = env?.session_id || activeSessionId || 'new';
      // The focus resubmit keys on `focus_token` server-side; `final_question`
      // is only the cache-miss fallback. When the readers ran on a pre-reader
      // CLARIFIED question, `env.clarification` holds it — that's the correct
      // fallback (the cached reader outputs were produced against it). Passing
      // it also keeps the "Clarified: …" callout visible through the focus
      // resubmit in-flight phase (Bug 1), since `runPrimingTurn` sets
      // `inFlightClarification` when final ≠ original. When there was no
      // pre-reader clarification, `env.clarification` is null → fall back to
      // the original query → no callout (final === original), which is correct.
      const finalQuestion = env?.clarification ?? original;
      if (env?.session_id && env.session_id !== activeSessionId) {
        setActiveSessionId(env.session_id);
      }
      setPendingTopicClarification(null);
      void runPrimingTurn(target, original, finalQuestion, regime, {
        focusToken,
        topicFocus,
      });
    },
    [pendingTopicClarification, activeSessionId, runPrimingTurn]
  );

  return {
    handleSend,
    handleResolveClarification,
    handleResolveTopicFocus,
  };
}

export type HomeChatComposer = ReturnType<typeof useHomeChatComposer>;
